import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReportSelector from '../components/ReportSelector';
import DimensionSelector from '../components/DimensionSelector';
import MetricSelector from '../components/MetricSelector';
import AnalyticsTable from '../components/AnalyticsTable';
import ExportCSV from '../components/ExportCSV';
import { fetchTopicStatsReport, fetchQuestionCountReport } from '../api/Reports';
import { filterNodes, sortNodes } from '../utils/analyticsUtils';
import SideBar from '../components/SideBar';
import { Components } from '../assets/Dimensions';

const DEFAULT_DIMENSIONS = [];
const user = JSON.parse(localStorage.getItem("user"));
const userAdminToken = sessionStorage.getItem("jwtP");
const generateId = () => Math.random().toString(36).substr(2, 9);

const transformData = (data, level = 0, parentDimensions = {}) => {
  if (!data || !Array.isArray(data)) return [];

  return data.map(row => {
    const currentDimensions = { ...parentDimensions, ...(row.Dimensions || {}) };
    let children = [];
    if (row.Breakdowns && row.Breakdowns.length > 0) {
      children = transformData(row.Breakdowns, level + 1, currentDimensions);
    }
    
    return {
      id: generateId(),
      level: level,
      dimensions: currentDimensions,
      metrics: row.Metrics || {},
      children: children
    };
  });
};

const mapFiltersToBackend = (filters) => {
  const mapping = {
    "Class": "ClassId",
    "ExamType": "ExamType",
    "SegmentId": "SegmentId",
    "CourseId": "CourseId",
    "SegmentCourseId": "SegmentCourseId",
    "AchievementId": "AchievementId",
    "AchievementVideoId": "AchievementVideoId",
    "PartName": "PartName"
  };

  const mapped = {};
  Object.keys(filters).forEach(key => {
    const backendKey = mapping[key] || key;
    mapped[backendKey] = filters[key];
  });
  return mapped;
};

const Spinner = React.memo(() => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '10px' }}>
    <svg width="40" height="40" viewBox="0 0 50 50" style={{ animation: 'rotate 2s linear infinite' }}>
      <circle
        cx="25"
        cy="25"
        r="20"
        fill="none"
        stroke="#28a745"
        strokeWidth="5"
        strokeLinecap="round"
        style={{ strokeDasharray: '90, 150', animation: 'dash 1.5s ease-in-out infinite' }}
      />
    </svg>
    <style>{`
      @keyframes rotate {
        100% { transform: rotate(360deg); }
      }
      @keyframes dash {
        0% { stroke-dasharray: 1, 150; stroke-dashoffset: 0; }
        50% { stroke-dasharray: 90, 150; stroke-dashoffset: -35; }
        100% { stroke-dasharray: 90, 150; stroke-dashoffset: -124; }
      }
    `}</style>
  </div>
));

const AnalyticsPage = () => {
  const [reportType, setReportType] = useState('topicStats');
  const [selectedDimensions, setSelectedDimensions] = useState(DEFAULT_DIMENSIONS);
  const [selectedMetrics, setSelectedMetrics] = useState(['Count']);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiFilters, setApiFilters] = useState({});
  const [localFilters, setLocalFilters] = useState({});
  const [sortRules, setSortRules] = useState([]);
  
  const handleReportTypeChange = useCallback((newType) => {
    if (newType === reportType) return;
    setRows([]); // Clear existing data immediately
    setLoading(true); // Trigger loader instantly
    setReportType(newType);
    setSelectedDimensions([]);
    setApiFilters({});
    setLocalFilters({});
    setSortRules([]);
    if (newType === 'topicStats') {
      setSelectedMetrics(['Count']);
    } else {
      setSelectedMetrics(['TotalCount', 'VideoCount', 'ImageCount', 'CMSReportCount', 'BscReportCount']);
    }
  }, [reportType]);

  const handleRunReport = useCallback(async () => {
    setLoading(true);
    
    try {
      let response;
      if (reportType === 'topicStats') {
        const payload = {
          extraGroupBy: selectedDimensions,
          metrics: selectedMetrics,
          filter: mapFiltersToBackend(apiFilters)
        };
        response = await fetchTopicStatsReport(payload, user.Token);
      } else if (reportType === 'questionCount') {
        const payload = {
          extraGroupBy: selectedDimensions,
          metrics: selectedMetrics, // Metrics are now selectable for this report type too
          filter: mapFiltersToBackend(apiFilters)
        };
        response = await fetchQuestionCountReport(payload, user.Token);
      }

      console.log("response", response);

      if (response && response.data && response.data.ResponseData && response.data.ResponseData.Rows) {
        const transformedData = transformData(response.data.ResponseData.Rows);
        setRows(transformedData);
      } else {
        setRows([]);
        console.error("Invalid response format", response);
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [reportType, selectedDimensions, selectedMetrics, apiFilters]);

  useEffect(() => {
    handleRunReport();
  }, [handleRunReport]);

  // Extract dimensions from the first row of data if available, to handle default dimensions
  let actualDimensions = selectedDimensions;
  if (selectedDimensions.length === 0 && rows.length > 0) {
    actualDimensions = Object.keys(rows[0].dimensions);
  } else if (rows.length > 0) {
     // If we have data, we'll ensure we show the dimensions that exist
     const availableDimsFromData = Object.keys(rows[0].dimensions);
     
     // Only use dimensions from data if we haven't selected anything or the selected dimensions don't match the data
     if (availableDimsFromData.length > 0) {
        // Collect all unique dimension keys from the first row and its nested children
        const extractAllDims = (row, currentDims = new Set()) => {
            Object.keys(row.dimensions || {}).forEach(k => currentDims.add(k));
            if (row.children && row.children.length > 0) {
                extractAllDims(row.children[0], currentDims);
            }
            return Array.from(currentDims);
        };
        actualDimensions = extractAllDims(rows[0]);
     }
  }

  // To make sure columns are ordered nicely, we can use actualDimensions
  let dimensionsCols = actualDimensions;
  
  let metricsCols = selectedMetrics;

  const columns = [...dimensionsCols, ...metricsCols];

  const filteredData = useMemo(() => filterNodes(rows, localFilters), [rows, localFilters]);
  const processedData = useMemo(() => sortNodes(filteredData, sortRules), [filteredData, sortRules]);

  return (
    <>
    <SideBar />
    <div style={{marginLeft: Components.SideBarWidth + 15, padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h1>Generic Analytics Reporting</h1>
      
      <div style={{ padding: '20px', border: '1px solid #ccc', borderRadius: '8px', marginBottom: '20px' }}>
        <ReportSelector 
          reportType={reportType} 
          setReportType={handleReportTypeChange} 
        />
        
        <DimensionSelector 
          reportType={reportType} 
          selectedDimensions={selectedDimensions} 
          setSelectedDimensions={setSelectedDimensions} 
        />
        
        <MetricSelector 
          reportType={reportType} 
          selectedMetrics={selectedMetrics} 
          setSelectedMetrics={setSelectedMetrics} 
          selectedDimensions={selectedDimensions}
        />
        
        <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
          <button 
            onClick={handleRunReport} 
            disabled={loading}
            style={{ 
              padding: '10px 20px', 
              backgroundColor: '#007bff', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: 'bold'
            }}
          >
            {loading ? 'Loading...' : 'Generate Report'}
          </button>
          
          <ExportCSV data={processedData} columns={columns} />
        </div>
      </div>
      
      <div style={{ position: 'relative', minHeight: '200px' }}>
        <div style={{ opacity: loading ? 0.4 : 1, transition: 'opacity 0.2s', pointerEvents: loading ? 'none' : 'auto' }}>
          <AnalyticsTable 
            data={processedData} 
            rawData={rows}
            columns={columns} 
            dimensions={dimensionsCols} 
            metrics={metricsCols} 
            apiFilters={apiFilters}
            setApiFilters={setApiFilters}
            localFilters={localFilters}
            setLocalFilters={setLocalFilters}
            sortRules={sortRules}
            setSortRules={setSortRules}
          />
        </div>

        {loading && (
          <div style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center', 
            alignItems: 'center', 
            backgroundColor: 'rgba(255,255,255,0.4)',
            zIndex: 100,
            borderRadius: '4px'
          }}>
            <Spinner />
            <div style={{ marginTop: '10px', fontWeight: 'bold', color: '#28a745', fontSize: '16px' }}>
              {rows.length > 0 ? 'Updating data...' : 'Loading data...'}
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default AnalyticsPage;
